import React from 'react';
import { useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PortraitIcon from '@mui/icons-material/Portrait';
import AuthStatus from './AuthStatus';
import { useUserRole } from './UserRoleProvider';
import './Navigation.css';

interface AdminNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

interface AdminNavItem {
  label: string;
  url: string;
  icon: React.ReactNode;
  external?: boolean;
  // Listed only for someone who can use it.
  requires?: 'canEditBranches';
}

// Contentful is edited in Contentful's own web app; this is only a way to get there.
const CONTENTFUL_URL = 'https://app.contentful.com/spaces/oj5eft50x9zv/home';

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { label: 'User Management', url: '/admin/users', icon: <GroupIcon fontSize="small" /> },
  { label: 'Branch Management', url: '/admin/branches/edit', icon: <BusinessIcon fontSize="small" />, requires: 'canEditBranches' },
  { label: 'Agency Photos', url: '/admin/agency-photos', icon: <PortraitIcon fontSize="small" /> },
  { label: 'Agency Redirects', url: '/admin/agency-redirects', icon: <AltRouteIcon fontSize="small" /> },
  { label: 'Documents', url: '/admin/documents', icon: <SummarizeIcon fontSize="small" /> },
  { label: 'Logs', url: '/admin/logs', icon: <ManageSearchIcon fontSize="small" /> },
  { label: 'Data Viewer', url: '/admin/data-viewer', icon: <StorageIcon fontSize="small" /> },
  { label: 'API Control Center', url: '/admin/control-center', icon: <TuneIcon fontSize="small" /> },
  { label: 'Underwriting Guidelines', url: '/admin/underwriting', icon: <FactCheckIcon fontSize="small" /> },
  { label: 'Contentful Access', url: CONTENTFUL_URL, icon: <EditNoteIcon fontSize="small" />, external: true },
];

// The left nav of the admin area, shown in place of the main one. It keeps the main nav's classes so
// the mobile drawer, its backdrop and the collapse toggle behave the same here.
const AdminNavigation: React.FC<AdminNavigationProps> = ({ isOpen, onClose }) => {
  const currentPath = useLocation().pathname;
  const role = useUserRole();
  const items = ADMIN_NAV_ITEMS.filter((item) => !item.requires || role[item.requires]);

  // By prefix, so a user's detail page lights up User Management and every documents screen lights
  // up Documents.
  const isActive = (url: string) => currentPath === url || currentPath.startsWith(`${url}/`);

  return (
    <nav className={`sidebar-navigation ${isOpen ? 'open' : ''}`} aria-label="Admin">
      <ul className="sidebar-links">
        <li>
          <a href="/" onClick={onClose}><ArrowBackIcon fontSize="small" /> Back to AgencyOS</a>
        </li>
        <li>
          <a href="/admin" onClick={onClose}>
            <strong>Admin</strong>
          </a>
        </li>
        {items.map(({ label, url, icon, external }) => (
          <li key={label}>
            {external ? (
              <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClose}>{icon} {label}</a>
            ) : (
              <a href={url} className={isActive(url) ? 'active' : ''} onClick={onClose}>{icon} {label}</a>
            )}
          </li>
        ))}
      </ul>

      <AuthStatus />
    </nav>
  );
};

export default AdminNavigation;
